import { apiFetch } from './client.js';
import type { SignatureView } from './signature-view.js';

/**
 * My Team (Teams stage 3, mockup *Teams*, boards 5-8 and 13-14, approved
 * 25-26 October), for athletes, team coaches and parents alike. The server
 * decides what each may see (read-my-team.ts); the screens draw what comes.
 */

type GetToken = () => Promise<string | null>;

export type TeamRole = 'head_coach' | 'assistant' | 'player';

export interface MyTeamPillRow {
  team_id: string;
  name: string;
  role: TeamRole;
  logo_url: string | null;
}

export interface RosterCodes {
  motor_profile: string | null;
  motivation_1: string | null;
  motivation_2: string | null;
}

export interface MyTeamMember {
  client_id: string;
  first_name: string;
  last_name: string;
  role: TeamRole;
  you: boolean;
  jersey_number: string | null;
  positions: string[];
  photo_url: string | null;
  codes: RosterCodes | null;
  opens: boolean;
}

export interface MyTeam {
  team_id: string;
  name: string;
  logo_url: string | null;
  viewer_role: TeamRole;
  members: MyTeamMember[];
}

export interface TeamAthlete {
  client_id: string;
  first_name: string;
  last_name: string;
  team_name: string;
  jersey_number: string | null;
  positions: string[];
  photo_url: string | null;
  can_edit: boolean;
  signature: SignatureView | null;
}

/** What the head coach saves from Edit (board 14). */
export interface TeamAthleteSave {
  jersey_number: string;
  positions: string[];
  bats: 'L' | 'R' | 'S' | null;
  throws: 'L' | 'R' | null;
}

/** The signed-in person's own teams (board 5). */
export function listMyTeams(getToken: GetToken): Promise<{ teams: MyTeamPillRow[] }> {
  return apiFetch('/api/teams', getToken);
}

/** A child's teams and their two switches, for either parent (board 8; lessons: 030, board a). */
export function listChildTeams(
  getToken: GetToken,
  childId: string,
): Promise<{ teams: MyTeamPillRow[]; teammates_can_see: boolean; team_coaches_see_lessons: boolean }> {
  return apiFetch(`/api/teams?person=${encodeURIComponent(childId)}`, getToken);
}

export function readMyTeam(getToken: GetToken, teamId: string): Promise<MyTeam> {
  return apiFetch(`/api/teams/${encodeURIComponent(teamId)}`, getToken);
}

const playerPath = (teamId: string, clientId: string) => `/api/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(clientId)}`;

export function readTeamAthlete(getToken: GetToken, teamId: string, clientId: string): Promise<TeamAthlete> {
  return apiFetch(playerPath(teamId, clientId), getToken);
}

export function saveTeamAthlete(getToken: GetToken, teamId: string, clientId: string, save: TeamAthleteSave): Promise<TeamAthlete> {
  return apiFetch(playerPath(teamId, clientId), getToken, { method: 'PUT', body: JSON.stringify(save) });
}

/** For saveTeamImage (coach-teams.ts): the head coach's photo route. */
export const teamAthletePhotoPath = (teamId: string, clientId: string) => `${playerPath(teamId, clientId)}/photo`;

export function saveTeammatesCanSee(getToken: GetToken, childId: string, on: boolean): Promise<{ teammates_can_see: boolean }> {
  return apiFetch(`/api/people/${encodeURIComponent(childId)}/teammates-can-see`, getToken, { method: 'PUT', body: JSON.stringify({ on }) });
}

/** Positions written out on the profile ("positions should be clear and stated", Matthew, 26 October). */
export const POSITION_NAMES: Record<string, string> = {
  P: 'Pitcher',
  C: 'Catcher',
  '1B': 'First base',
  '2B': 'Second base',
  '3B': 'Third base',
  SS: 'Shortstop',
  LF: 'Left field',
  CF: 'Center field',
  RF: 'Right field',
  UT: 'Utility',
};

/**
 * Where a My Team page's ‹ goes: the child's page it was opened from, or Home
 * (a fixed parent page, never history - ruled 25 October).
 */
export function teamBack(child: string | null): { to: string; query: string } {
  return child ? { to: `/account/people/${child}`, query: `?child=${child}` } : { to: '/', query: '' };
}
